import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Circle } from 'react-leaflet';
import L from 'leaflet';

import { reportsApi, geofencesApi, companiesApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface GeofenceViolation {
  id: string;
  type: 'GEOFENCE_ENTER' | 'GEOFENCE_EXIT';
  title: string;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  device: {
    id: string;
    imei: string;
    name: string | null;
    vehiclePlate: string | null;
  };
  geofence: {
    id: string;
    name: string;
  } | null;
}

interface Geofence {
  id: string;
  name: string;
  geometry: {
    type: 'Polygon' | 'Circle';
    coordinates?: number[][][];
    center?: [number, number];
    radius?: number;
  };
  isActive: boolean;
}

interface Company {
  id: string;
  name: string;
  code: string;
}

// Custom icons for enter/exit
const enterIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #22c55e; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">↓</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const exitIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #ef4444; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">↑</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export function GeofenceReportPage() {
  const user = useAuthStore((state) => state.user);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedGeofence, setSelectedGeofence] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(
    format(subDays(new Date(), 7), 'yyyy-MM-dd')
  );
  const [dateTo, setDateTo] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  // Fetch companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.list({ limit: 100 });
      return response.data;
    },
    enabled: user?.role === 'ADMIN' || user?.role === 'SUPERADMIN',
  });

  // Fetch geofences
  const { data: geofencesData } = useQuery({
    queryKey: ['geofences', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100, isActive: true };
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await geofencesApi.list(params);
      return response.data;
    },
  });

  // Fetch violations
  const { data: violationsData, isLoading } = useQuery({
    queryKey: ['geofence-violations', selectedCompany, selectedGeofence, dateFrom, dateTo],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        limit: 200,
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo + 'T23:59:59').toISOString(),
      };
      if (selectedCompany) params.companyId = selectedCompany;
      if (selectedGeofence) params.geofenceId = selectedGeofence;
      const response = await reportsApi.getGeofenceViolations(params);
      return response.data;
    },
  });

  const companies = (companiesData?.data || []) as Company[];
  const geofences = (geofencesData?.data || []) as Geofence[];
  const violations = (violationsData?.data || []) as GeofenceViolation[];

  // Calculate stats
  const enterCount = violations.filter((v) => v.type === 'GEOFENCE_ENTER').length;
  const exitCount = violations.filter((v) => v.type === 'GEOFENCE_EXIT').length;

  // Get selected geofence for map display
  const selectedGeofenceData = geofences.find((g) => g.id === selectedGeofence);

  // Get center for map
  const getMapCenter = (): [number, number] => {
    if (selectedGeofenceData?.geometry) {
      if (selectedGeofenceData.geometry.type === 'Circle' && selectedGeofenceData.geometry.center) {
        return [selectedGeofenceData.geometry.center[1], selectedGeofenceData.geometry.center[0]];
      }
      if (selectedGeofenceData.geometry.type === 'Polygon' && selectedGeofenceData.geometry.coordinates) {
        const coords = selectedGeofenceData.geometry.coordinates[0];
        const avgLat = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
        const avgLon = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
        return [avgLat, avgLon];
      }
    }
    if (violations.length > 0) {
      const validViolations = violations.filter((v) => v.latitude && v.longitude);
      if (validViolations.length > 0) {
        return [validViolations[0].latitude!, validViolations[0].longitude!];
      }
    }
    return [18.4861, -69.9312]; // Default: Santo Domingo
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reporte de Geocercas</h1>
        <p className="text-gray-600">Violaciones de entrada y salida de geocercas</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
              <select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setSelectedGeofence('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Geocerca</label>
            <select
              value={selectedGeofence}
              onChange={(e) => setSelectedGeofence(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              {geofences.map((geofence) => (
                <option key={geofence.id} value={geofence.id}>
                  {geofence.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm font-medium text-gray-500">Total Eventos</div>
          <div className="text-2xl font-bold text-gray-900">{violations.length}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm font-medium text-gray-500">Entradas</div>
          <div className="text-2xl font-bold text-green-600">{enterCount}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm font-medium text-gray-500">Salidas</div>
          <div className="text-2xl font-bold text-red-600">{exitCount}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Map */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-medium">Mapa de Eventos</h3>
          </div>
          <div className="h-96">
            <MapContainer
              center={getMapCenter()}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Show selected geofence */}
              {selectedGeofenceData?.geometry.type === 'Polygon' &&
                selectedGeofenceData.geometry.coordinates && (
                  <Polygon
                    positions={selectedGeofenceData.geometry.coordinates[0].map(
                      (coord) => [coord[1], coord[0]] as [number, number]
                    )}
                    pathOptions={{
                      color: '#3b82f6',
                      fillColor: '#3b82f6',
                      fillOpacity: 0.2,
                    }}
                  />
                )}

              {selectedGeofenceData?.geometry.type === 'Circle' &&
                selectedGeofenceData.geometry.center &&
                selectedGeofenceData.geometry.radius && (
                  <Circle
                    center={[
                      selectedGeofenceData.geometry.center[1],
                      selectedGeofenceData.geometry.center[0],
                    ]}
                    radius={selectedGeofenceData.geometry.radius * 1000}
                    pathOptions={{
                      color: '#3b82f6',
                      fillColor: '#3b82f6',
                      fillOpacity: 0.2,
                    }}
                  />
                )}

              {/* Show violation markers */}
              {violations
                .filter((v) => v.latitude && v.longitude)
                .map((violation) => (
                  <Marker
                    key={violation.id}
                    position={[violation.latitude!, violation.longitude!]}
                    icon={violation.type === 'GEOFENCE_ENTER' ? enterIcon : exitIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="font-medium">
                          {violation.type === 'GEOFENCE_ENTER' ? 'Entrada' : 'Salida'}
                        </div>
                        <div>{violation.device.name || violation.device.imei}</div>
                        <div className="text-gray-500">{violation.geofence?.name}</div>
                        <div className="text-xs text-gray-400">
                          {format(new Date(violation.createdAt), 'dd/MM/yyyy HH:mm')}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-medium">Lista de Eventos ({violations.length})</h3>
          </div>
          {isLoading ? (
            <div className="p-6 text-center">Cargando...</div>
          ) : violations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No hay eventos en el periodo seleccionado
            </div>
          ) : (
            <div className="overflow-y-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dispositivo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Geocerca
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {violations.map((violation) => (
                    <tr key={violation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            violation.type === 'GEOFENCE_ENTER'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {violation.type === 'GEOFENCE_ENTER' ? '↓ Entrada' : '↑ Salida'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium">
                          {violation.device.name || 'Sin nombre'}
                        </div>
                        <div className="text-xs text-gray-500">{violation.device.imei}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {violation.geofence?.name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(violation.createdAt), 'dd/MM/yyyy HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
